/**
 * event-select.js
 */

import React from 'react';
import axios from 'axios';
import { IconButton, List } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  useHistory,
  useParams,
} from "react-router-dom";
import { parse, formatISO, addMonths } from 'date-fns';
import { Types } from 'utils/constants';
import { dateParamFormat } from 'utils/datetime';
import { sleep } from 'utils/misc';
import { shouldRenderMobileLayout } from 'utils/app-context';
import { makeLocation } from 'utils/routing';
import useRecordStore from 'contexts/record-store';
import Icon from 'components/icon';
import DrawerContentBuilder from 'components/menu-drawer/drawer-content-builder';
import DesktopContentHeader from 'components/menu-drawer/desktop-content-header';
import MobilePageTitle from 'components/menu-drawer/mobile-page-title';
import DrawerContent from 'components/menu-drawer/drawer-content';
import DrawerActionButton from 'components/menu-drawer/drawer-action-button';
import TrainingWizardStepper from 'components/training/training-drawer/training-wizard-stepper';
import EventMenuItem from '../menu-item';
import EventTabs from '../container/event-tabs';


const useStyles = makeStyles(theme => ({
  eventList: {
    marginTop: theme.spacing(3),
    '& .MuiChip-root': {
      border: `1px solid ${theme.palette.grey.colors[7]}`
    },
    '& .active': {
      backgroundColor: theme.palette.sys.primaryContainer
    },
    [theme.breakpoints.up('xs')]: {
      maxWidth: 350
    }
  }
}));


export default function EventSelect(props) {
  const {
    drawerState,
    onClose,
    wizard,
    basePath,
  } = props;
  const classes = useStyles();
  let history = useHistory();
  const recordStore = useRecordStore();
  const params = useParams();

  const [eventOptions, setEventOptions] = React.useState([]);
  const [eventId, setEventId] = React.useState();
  const eventRecord = recordStore.useRecord(Types.Record.EVENT, eventId);
  const hasEventOptions = eventOptions.length > 0;
  const hasValidEventId = eventId > 0;
  const hasEvent = Boolean(eventRecord.get());

  // get events on selected date
  const dateParam = new URLSearchParams(window.location.search).get('date');
  const selectedDate = dateParam
    ? parse(dateParam, dateParamFormat, new Date())
    : new Date();
  const selectedDateState = recordStore.useDate(selectedDate).ornull;
  const selectedDateEvents = selectedDateState 
    ? selectedDateState.records.get().filter(r => r.recordType === Types.Record.EVENT) 
    : null;  
  const selectedDateEventCount = dateParam && selectedDateEvents ? selectedDateEvents.length : null;


  React.useEffect(() => {
    if (params.eventId) {
      const eventId = parseInt(params.eventId);
      if (eventId > 0) {
        setEventId(parseInt(params.eventId));
      } 
    }
  }, [params.eventId]);


  React.useEffect(() => {
    if (hasEventOptions) return;

    setTimeout(() => drawerState.loading.content.set(true));
    (async () => {
      await sleep(200);

      if (!dateParam || selectedDateEventCount === 0) {
        try {
          const response = await axios.get(`/training/event/editable`, { params: { startDate: formatISO(addMonths(new Date(), -3)) } });
          const events = response.data;
          setEventOptions(events);

          if (!hasValidEventId && events.length > 0) {
            setEventId(events[0].id);
        
            history.replace(makeLocation(
              basePath,
              'training',
              'wizardSelectEvent',
              { eventId: events[0].id },
              history.location,
              dateParam ? { date: dateParam } : null
            ));
          } else if (events.length === 0) {
            // failsafe - redirect to create event
            history.replace(makeLocation(
              basePath,
              'training',
              'wizardCreateEvent',
              { eventId: 0 },
              history.location,
              dateParam ? { date: dateParam } : null
            ));
          }
        } catch (e) {
          // failsafe - redirect to create event
          history.replace(makeLocation(
            basePath,
            'training',
            'wizardCreateEvent',
            { eventId: 0 },
            history.location,
            dateParam ? { date: dateParam } : null
          ));
        }

        drawerState.loading.content.set(false);
        
      } else if (selectedDateEventCount > 0) {
        setEventOptions(selectedDateEvents);

        if (!hasValidEventId) {
          history.replace(makeLocation(
            basePath,
            'training',
            'wizardSelectEvent',
            { eventId: selectedDateEvents[0].id },
            history.location,
            dateParam ? { date: dateParam } : null
          ));
        }

        setTimeout(() => drawerState.loading.content.set(false));
      }

    })();

  // drawerState is hookstate setter.
  // history causes too much rendering - not necessary here.
  // selectedDateEvents is complex so using selectedDateEventCount to trigger render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateParam, selectedDateEventCount, hasValidEventId, hasEventOptions, basePath]);


  const onSave = React.useCallback(() => {
    wizard.onSave(eventRecord.get(), history, basePath);

  // eventRecord is hookstate obj
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, wizard, history, basePath]);


  const nextButton = React.useMemo(() => (
    <DrawerActionButton
      level={1}
      onClick={onSave}
      data-testid='select-event-next'
    >
      Next
    </DrawerActionButton>
  ), [onSave]);


  const closeButton = (
    <IconButton
      data-testid="mobile-drawer-close-button"
      size="small"
      style={{color: 'inherit'}}
      onClick={onClose}
    >
      <Icon type='close' />
    </IconButton>
  );


  const stickyHeader = (
    <>
      {/* mobile & wizard -> show new/existing tabs */}
      {(shouldRenderMobileLayout() && wizard) && 
      <EventTabs value='new' basePath={basePath} />}

      {/* desktop */}
      {!shouldRenderMobileLayout() &&
      <>
        {/* wizard -> show event tabs + actions */}
        {wizard && 
        <DesktopContentHeader 
          title={<EventTabs value='existing' basePath={basePath} />} 
          actions={nextButton} 
        />}

        {/* no wizard -> show regular title + actions */}
        {!wizard && 
        <DesktopContentHeader
          title={eventRecord.get()?.name}
          actions={nextButton}
        />}
      </>}
    </>
  );

  
  return (
    <>
      {(hasEvent && eventId !== null && eventOptions.length > 0) &&
      <>
        <DrawerContentBuilder
          mobileAppBar={{
            title: 'Event',
            leftActions: closeButton,
            rightActions: nextButton,
            recordGroupType: Types.RecordGroup.TRAINING,
          }}
          desktopHeader={{ 
            title: eventRecord.get()?.name,
            actions: nextButton
          }}
          stickyHeader={{ content: stickyHeader, offset: -20 }}
          children={(triggerElement, desktopHeader) => (
            <>
              {desktopHeader}
              <MobilePageTitle title={eventRecord.get().name} />
    
              <DrawerContent>

                {shouldRenderMobileLayout() &&
                <TrainingWizardStepper basePath={basePath} />}
    
                {triggerElement}

                {wizard &&
                <EventTabs value='existing' basePath={basePath} />}
    
                <List className={classes.eventList}>
                  {eventOptions.map(e => {
                    return <EventMenuItem
                      key={e.id}
                      record={e}
                      isActive={e.id === eventId}
                      hideChevron
                      to={makeLocation(
                        basePath,
                        'training',
                        'wizardSelectEvent',
                        { eventId: e.id },
                        history.location,
                        dateParam ? { date: dateParam } : null
                      )}
                    />
                  })}
                </List>
    
              </DrawerContent>
            </>
          )}
        />
      </>}
    </>
  );
}